import { scoreTier } from "./score-validation";
import type {
  BankedRewardClaim,
  LeaderboardEntry,
  LocalOnlineGameService,
  OnlineGameService,
  PlayerIdentity,
  RemoteBankedProfile,
  ScoreSubmission,
} from "./contracts";

type RemoteScoreRow = {
  player_id?: string;
  playerId?: string;
  score: number;
  metadata?: string | Record<string, unknown>;
  occurred_at?: string;
  occurredAt?: string;
};

/** Posts scores to a Cloudflare Worker; falls back to local cache on failure. */
export class RemoteOnlineGameService implements OnlineGameService {
  public constructor(
    private readonly baseUrl: string,
    private readonly identity: PlayerIdentity,
    private readonly local: LocalOnlineGameService,
  ) {}

  public getIdentity(): Promise<PlayerIdentity> {
    return this.local.getIdentity();
  }

  public register(username: string, password: string): Promise<PlayerIdentity> {
    return this.local.register(username, password);
  }

  public signIn(username: string, password: string): Promise<PlayerIdentity> {
    return this.local.signIn(username, password);
  }

  public async submitScore(submission: ScoreSubmission): Promise<void> {
    await this.local.submitScore(submission);
    try {
      const res = await fetch(`${this.baseUrl}/api/scores`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          playerId: this.identity.id,
          score: submission.score,
          metadata: submission.metadata ?? {},
          occurredAt: submission.occurredAt,
        }),
      });
      if (!res.ok) throw new Error(`score_post_${res.status}`);
    } catch {
      // Local cache already updated; remote sync is best-effort.
    }
  }

  public async getLeaderboard(limit = 20): Promise<LeaderboardEntry[]> {
    try {
      const res = await fetch(`${this.baseUrl}/api/leaderboard?limit=${limit}`);
      if (!res.ok) throw new Error(`leaderboard_${res.status}`);
      const data = (await res.json()) as { entries?: RemoteScoreRow[] };
      const rows = data.entries ?? [];
      if (rows.length === 0) return this.local.getLeaderboard(limit);
      return rows.map((row) => {
        const playerId = row.playerId ?? row.player_id ?? "unknown";
        const score = row.score;
        const metadata =
          typeof row.metadata === "string"
            ? (JSON.parse(row.metadata) as Record<string, string | number | boolean>)
            : (row.metadata as Record<string, string | number | boolean> | undefined);
        return {
          playerId,
          score,
          tier: scoreTier(score),
          occurredAt: row.occurredAt ?? row.occurred_at ?? new Date(0).toISOString(),
          metadata,
        };
      });
    } catch {
      return this.local.getLeaderboard(limit);
    }
  }

  public getBankedProfile(): Promise<RemoteBankedProfile | null> {
    return this.local.getBankedProfile();
  }

  public claimBankedRewards(claim: BankedRewardClaim): Promise<RemoteBankedProfile> {
    return this.local.claimBankedRewards(claim);
  }
}
